import React, { useState, useEffect, useRef, Fragment } from "react";
import { confirmSignIn } from "aws-amplify/auth";
import { normalizeEmail } from "../../../js/formUtils";

/**
 * EmailCodeSignIn Component - Handles AWS Amplify CONFIRM_SIGN_IN_WITH_EMAIL_CODE challenge
 *
 * Props:
 * @param {object} user - The AWS Cognito user object from sign-in response
 * @param {string} email - User's email address for display
 * @param {function} onEmailCodeSuccess - Callback function called after successful email code verification
 * @param {function} onCancel - Callback function to return to login
 */

function EmailOTP(props) {
	const codeLength = 6;
	const [email, setEmail] = useState(props.email || "");
	const [codeDigits, setCodeDigits] = useState(Array(codeLength).fill(""));
	const inputRefs = useRef([]);
	const firstInputRef = useRef(null);

	// State for the complete confirmation code
	const [confirmationCode, setConfirmationCode] = useState("");
	const [confirmationError, setConfirmationError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [userForOTP, setUserForOTP] = useState(null);
	// Mirrors isSubmitting for the reentrancy check in confirmSignInWithEmailCode.
	// Duplicate auto-submit timers (e.g. from onChange + onInput both firing for one
	// keystroke) can share the same stale render's closure, where the isSubmitting
	// state binding never updates — a ref is mutated in place so every closure sees
	// the latest value instead.
	const isSubmittingRef = useRef(false);

	useEffect(() => {
		if (typeof props.email === "string" && props.email !== "") {
			setEmail(props.email);
		}
	}, [props.email]);

	useEffect(() => {
		if (props.user) {
			setUserForOTP(props.user);
		}
	}, [props.user]);

	const clearState = () => {
		setConfirmationCode("");
		setConfirmationError("");
		setIsSubmitting(false);
		setCodeDigits(Array(codeLength).fill(""));

		// Clear the actual input field values
		inputRefs.current.forEach((input) => {
			if (input) {
				input.value = "";
			}
		});
	};
	/**
	 * Step 2: Confirm sign-in with email code using AWS Amplify
	 */
	const confirmSignInWithEmailCode = async (codeOverride) => {
		if (isSubmittingRef.current) return;

		const codeToSubmit = codeOverride ?? confirmationCode;
		if (!codeToSubmit || codeToSubmit.length !== 6) {
			setConfirmationError("Please enter a valid 6-digit verification code");
			return;
		}

		isSubmittingRef.current = true;
		setIsSubmitting(true);
		setConfirmationError("");

		try {
			const result = await confirmSignIn({
				challengeResponse: codeToSubmit,
			});

			// Call success callback to continue login flow. Await it so the button/inputs
			// stay disabled until the parent finishes its own post-verification work
			// (e.g. fetching permissions) and actually navigates away from this screen.
			if (props.onEmailCodeSuccess) {
				await props.onEmailCodeSuccess(result);
			}
		} catch (error) {
			console.error("Error verifying email code:", error);
			setConfirmationError(
				error.message || "Failed to verify email code. Please try again.",
			);
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	// Handle email normalization
	const handleEmailInput = (value) => {
		const normalized = normalizeEmail(value);
		setEmail(normalized);
		return normalized;
	};

	const handleCodeSubmit = (e) => {
		e.preventDefault();
		confirmSignInWithEmailCode();
	};

	// Focus first input on mount
	useEffect(() => {
		if (firstInputRef.current) {
			setTimeout(() => {
				firstInputRef.current.focus();
			}, 50);
		}
	}, []);

	const handleCodeInput = (e, index) => {
		const target = e.target;
		const value = target.value;

		// Only allow numeric input
		if (!/^\d*$/.test(value)) {
			return;
		}

		// Handle case when user types multiple digits
		if (value.length > 1) {
			const digits = value.split("");

			// Update current and subsequent inputs with the digits
			const newCodeDigits = [...codeDigits];

			for (let i = 0; i < digits.length && index + i < codeLength; i++) {
				newCodeDigits[index + i] = digits[i];
			}

			const code = newCodeDigits.join("");
			setCodeDigits(newCodeDigits);
			setConfirmationCode(code);

			// Focus on the next empty input or the last input
			const nextEmptyIndex = newCodeDigits.findIndex((digit) => digit === "");
			if (nextEmptyIndex !== -1 && nextEmptyIndex < codeLength) {
				inputRefs.current[nextEmptyIndex].focus();
			} else {
				inputRefs.current[codeLength - 1].focus();
			}

			// Auto-submit when complete (e.g. autofill delivering all digits at once)
			if (newCodeDigits.every((digit) => digit !== "")) {
				setTimeout(() => confirmSignInWithEmailCode(code), 100);
			}
		} else {
			// Handle single digit input
			const newCodeDigits = [...codeDigits];
			newCodeDigits[index] = value;
			setCodeDigits(newCodeDigits);

			// Move focus to next input if available
			if (value && index < codeLength - 1) {
				inputRefs.current[index + 1].focus();
			}

			// Notify parent of change
			const code = newCodeDigits.join("");
			setConfirmationCode(code);

			// Auto-submit when complete
			if (newCodeDigits.every((digit) => digit !== "")) {
				setTimeout(() => confirmSignInWithEmailCode(code), 100);
			}
		}
	};

	const handleKeyDown = (e, index) => {
		// Handle backspace
		if (e.key === "Backspace") {
			const newCodeDigits = [...codeDigits];

			// If current input is empty and not the first one, move to previous input
			if (newCodeDigits[index] === "" && index > 0) {
				newCodeDigits[index - 1] = "";
				setCodeDigits(newCodeDigits);
				inputRefs.current[index - 1].focus();
			} else {
				// Clear current input
				newCodeDigits[index] = "";
				setCodeDigits(newCodeDigits);
			}

			// Notify parent of change
			setConfirmationCode(newCodeDigits.join(""));
			e.preventDefault();
		}

		// Handle arrow keys
		if (e.key === "ArrowLeft" && index > 0) {
			inputRefs.current[index - 1].focus();
			e.preventDefault();
		}

		if (e.key === "ArrowRight" && index < codeLength - 1) {
			inputRefs.current[index + 1].focus();
			e.preventDefault();
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();

		const pastedData =
			(e.clipboardData && e.clipboardData.getData("text")) || "";
		const digits = pastedData.replace(/\D/g, "").split("").slice(0, codeLength);

		if (digits.length > 0) {
			const newCodeDigits = [...codeDigits];

			digits.forEach((digit, i) => {
				if (i < codeLength) {
					newCodeDigits[i] = digit;
				}
			});

			setCodeDigits(newCodeDigits);

			// Focus on the next empty input or the last filled input
			const nextEmptyIndex = newCodeDigits.findIndex((digit) => digit === "");
			if (nextEmptyIndex !== -1) {
				inputRefs.current[nextEmptyIndex].focus();
			} else {
				inputRefs.current[Math.min(digits.length, codeLength) - 1].focus();
			}

			// Notify parent of change
			const code = newCodeDigits.join("");
			setConfirmationCode(code);

			// Auto-submit when complete
			if (newCodeDigits.every((digit) => digit !== "")) {
				setTimeout(() => confirmSignInWithEmailCode(code), 100);
			}
		}
	};

	return (
		<div className="confirmation-container">
			<Fragment>
				<h3>Enter Verification Code</h3>
				<p>
					We've sent a verification code to <strong>{email}</strong>. Please
					enter it below to complete your sign-in.
				</p>

				{confirmationError && (
					<div className="error-message">{confirmationError}</div>
				)}

				<form onSubmit={handleCodeSubmit} className="verification-form">
					<div className="confirmation-form">
						<div className="confirmation-input-group">
							<label>Verification Code</label>
							<div className="verification-code-container">
								{codeDigits.map((digit, index) => (
									<input
										key={index}
										type="text"
										inputMode="numeric"
										pattern="[0-9]*"
										maxLength="1"
										value={digit}
										onChange={(e) => handleCodeInput(e, index)}
										onInput={(e) => handleCodeInput(e, index)}
										onKeyDown={(e) => handleKeyDown(e, index)}
										onFocus={(e) => e.currentTarget.select()}
										onPaste={(e) => handlePaste(e)}
										ref={(el) => {
											if (el) {
												inputRefs.current[index] = el;
												if (index === 0) firstInputRef.current = el;
											}
										}}
										disabled={isSubmitting}
										className="code-input"
										aria-label={`Digit ${index + 1}`}
										autoComplete="off"
									/>
								))}
							</div>
						</div>
						<div className="verify-actions">
							<button
								type="submit"
								className="submit-button"
								disabled={confirmationCode.length !== 6 || isSubmitting}>
								{isSubmitting ? "Verifying..." : "Verify Code"}
							</button>
						</div>
					</div>
				</form>
				<div className="form-links">
					<a
						href="#"
						className={`form-text-link ${isSubmitting ? "disabled" : ""}`}
						onClick={(e) => {
							e.preventDefault();
							if (!isSubmitting) {
								props.onCancel();
							}
						}}>
						Back to Login
					</a>
				</div>
			</Fragment>
		</div>
	);
}

export default EmailOTP;
